import LanguageToggle from "@/components/ui/language-toggle";
import { keys, StorageUtils } from "@/features/shared/storage/storage.utils";
import { getNextReleaseNoteToShow, ReleaseNote, ReleaseNotesState } from "@/services/release-notes.services";
import { Button, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@EspaceParkQC/sdk-react";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";

export default function Announcement() {
  const [open, setOpen] = useState(false);
  const [currentReleaseNote, setCurrentReleaseNote] = useState<ReleaseNote | null>(null);
  const { i18n, t } = useTranslation("main");

  useEffect(() => {
    const loadReleaseNote = async () => {
      const stateString = StorageUtils.getItem(keys.RELEASE_NOTES_STATE);
      const state: ReleaseNotesState = stateString ? JSON.parse(stateString) : {};
      const language = i18n.language || "en";
      const nextNote = await getNextReleaseNoteToShow(state, language);
      if (nextNote) {
        setCurrentReleaseNote(nextNote);
        setOpen(true);
      }
    };

    void loadReleaseNote();
  }, [i18n.language]);

  const handleDismiss = () => {
    if (!currentReleaseNote) return;
    const stateString = StorageUtils.getItem(keys.RELEASE_NOTES_STATE);
    const state: ReleaseNotesState = stateString ? JSON.parse(stateString) : {};
    const updatedState = { ...state, [currentReleaseNote.id]: { dismissed: true } };
    StorageUtils.setItem(keys.RELEASE_NOTES_STATE, JSON.stringify(updatedState));
    setOpen(false);
    setCurrentReleaseNote(null);
  };

  const date = useMemo(() => {
    if (!currentReleaseNote) return "";
    const date = currentReleaseNote.id.split(".")[0];
    return dayjs(date)
      .locale(i18n.language)
      .format(i18n.language === "fr" ? "D MMMM YYYY" : "MMM D, YYYY");
  }, [currentReleaseNote, i18n.language]);

  return currentReleaseNote ? (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleDismiss()}>
      <DialogContent className="w-[90%] h-[90%] max-w-none max-h-none p-0">
        <div className="md:p-12 p-6 overflow-auto">
          <LanguageToggle />
          <DialogHeader className="mt-4">
            <DialogTitle className="text-left text-lg font-semibold">{t("announcement.title")}</DialogTitle>
            <DialogDescription className="text-left text-sm text-muted-foreground">{date}</DialogDescription>
          </DialogHeader>

          <div className="prose prose-sm">
            <ReactMarkdown>{currentReleaseNote.content}</ReactMarkdown>
          </div>
        </div>

        <DialogFooter className="md:p-12 p-6 pt-0">
          <DialogClose asChild>
            <Button className="w-full">{t("announcement.got-it")}</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ) : null;
}
